// Typed, validated application environment (contract 01 §5). This is the ONLY place
// `import.meta.env` is read. Required keys are validated at boot; a missing/empty one
// throws EnvValidationError so the app fails fast and never mounts silently (§14).

export interface AppEnv {
  MODE: 'development' | 'test' | 'production'
  BASE_URL: string
  API_APP_URL: string // Django app API
  API_RAG_URL?: string // Optional future split-service seam; ledger B5 keeps current traffic on appHttp
  API_CONTROLLER_URL: string // controller / report service
  APP_NAME: string
  APP_ICON: string
  API_APP_FRONT_URL?: string
  // v3 admin panel origin. Its own host, NOT derivable from API_APP_FRONT_URL: that one is an
  // analytics field baked to the staging front at image build time (`main.yaml`), and module 19
  // never comes to v4 (`router/ingress/pendingCutover.ts` — `/admin/*` is permanent legacy).
  // Absent = this deployment has no admin panel, so v4 renders no entry point to one.
  API_ADMIN_URL?: string
  APP_VERSION_KEY?: string
  CREDIT_BOOK_CALL_URL?: string
  CREDIT_SELF_SERVE_ENABLED: boolean
  /**
   * Turns the report/request perf console on for the whole build (ORP-22839). Off unless
   * `VITE_PERF_LOG=true`, and never set in production — the per-browser switch for a deployed
   * environment is `localStorage['orphex:perf']`, which needs no rebuild.
   */
  PERF_LOG: boolean
  GTM_ID?: string
  SENTRY_DSN?: string
  INTERCOM_APP_ID?: string
}

/** The raw, untyped source shape — a subset of `import.meta.env`. */
export type RawEnv = Record<string, string | undefined>

const REQUIRED_KEYS = [
  'VITE_API_APP_URL',
  'VITE_API_CONTROLLER_URL',
  'VITE_APP_NAME',
  'VITE_APP_ICON',
] as const

export class EnvValidationError extends Error {
  readonly missing: readonly string[]
  constructor(missing: readonly string[]) {
    super(
      `Missing required environment variable(s): ${missing.join(', ')}. ` +
        'Copy .env.example to .env and fill them in.',
    )
    this.name = 'EnvValidationError'
    this.missing = missing
  }
}

/** Type guard: a present, non-blank string (narrows `string | undefined` → `string`). */
function present(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== ''
}

function normalizeMode(mode: string | undefined): AppEnv['MODE'] {
  return mode === 'development' || mode === 'test' ? mode : 'production'
}

/**
 * Pure parse + validate. Kept side-effect-free and independent of `import.meta.env`
 * so it is directly unit-testable (contract 01 §16 — fail-fast test).
 */
export function parseEnv(raw: RawEnv): AppEnv {
  const missing = REQUIRED_KEYS.filter((key) => !present(raw[key]))
  if (missing.length > 0) throw new EnvValidationError(missing)

  const req = (key: (typeof REQUIRED_KEYS)[number]): string => {
    const value = raw[key]
    if (!present(value)) throw new EnvValidationError([key]) // unreachable after the check above
    return value
  }

  const env: AppEnv = {
    MODE: normalizeMode(raw.MODE),
    BASE_URL: present(raw.BASE_URL) ? raw.BASE_URL : '/',
    API_APP_URL: req('VITE_API_APP_URL'),
    API_CONTROLLER_URL: req('VITE_API_CONTROLLER_URL'),
    APP_NAME: req('VITE_APP_NAME'),
    APP_ICON: req('VITE_APP_ICON'),
    CREDIT_SELF_SERVE_ENABLED: raw.VITE_CREDIT_SELF_SERVE_ENABLED === 'true',
    PERF_LOG: raw.VITE_PERF_LOG === 'true',
  }

  // Optional keys assigned only when present (exactOptionalPropertyTypes: no `undefined`).
  if (present(raw.VITE_API_RAG_URL)) env.API_RAG_URL = raw.VITE_API_RAG_URL
  if (present(raw.VITE_API_APP_FRONT_URL)) env.API_APP_FRONT_URL = raw.VITE_API_APP_FRONT_URL
  if (present(raw.VITE_API_ADMIN_URL)) env.API_ADMIN_URL = raw.VITE_API_ADMIN_URL
  if (present(raw.VITE_APP_VERSION_KEY)) env.APP_VERSION_KEY = raw.VITE_APP_VERSION_KEY
  if (present(raw.VITE_CREDIT_BOOK_CALL_URL)) {
    env.CREDIT_BOOK_CALL_URL = raw.VITE_CREDIT_BOOK_CALL_URL
  }
  if (present(raw.VITE_GTM_ID)) env.GTM_ID = raw.VITE_GTM_ID
  if (present(raw.VITE_SENTRY_DSN)) env.SENTRY_DSN = raw.VITE_SENTRY_DSN
  if (present(raw.VITE_INTERCOM_APP_ID)) env.INTERCOM_APP_ID = raw.VITE_INTERCOM_APP_ID

  return env
}

let cached: AppEnv | undefined

/** Validate `import.meta.env` once at boot. Call from bootstrap before anything else. */
export function validateEnv(): AppEnv {
  cached ??= parseEnv(import.meta.env as unknown as RawEnv)
  return cached
}

/** Access the already-validated env. Throws if called before validateEnv(). */
export function useEnv(): AppEnv {
  if (!cached) throw new Error('env accessed before validateEnv() — call it first in bootstrap.ts')
  return cached
}
